import React from "react";
import {
  X,
  ChefHat,
  ShoppingBasket,
  Snowflake,
  ClipboardList,
  BarChart3,
  Users,
  Settings,
  List,
  Store,
  LogOut,
  Download,
  Globe,
  BookOpen,
} from "lucide-react";
import useModalBackHandler from "../../hooks/useModalBackHandler";

const SidebarBtn = ({ active, onClick, icon: Icon, label, desc, color, bgColor, customStyle }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-200 text-left ${
      active
        ? customStyle || "bg-orange-100 border-l-4 border-orange-500 shadow-sm"
        : "hover:bg-gray-50/50 text-gray-600"
    }`}
  >
    <div
      className={`p-2 rounded-lg ${
        active
          ? bgColor || "bg-white text-orange-600"
          : "bg-gray-100/50 text-gray-500"
      }`}
    >
      <Icon className={`w-5 h-5 ${active ? color || "" : ""}`} />
    </div>
    <div>
      <p
        className={`font-bold text-sm ${
          active ? "text-gray-900" : "text-gray-800"
        }`}
      >
        {label}
      </p>
      {desc && <p className="text-[10px] text-gray-400 font-medium">{desc}</p>}
    </div>
  </button>
);

const Sidebar = ({
  isOpen,
  onClose,
  setView,
  activeView,
  userRole,
  onLogout,
  installPwa,
  globalConfig
}) => {
  useModalBackHandler(isOpen, onClose);
  
  // Extraer los módulos activos de manera segura
  const modules = globalConfig?.modules || {};

  const handleNav = (view) => {
    setView(view);
    if (window.innerWidth < 768) onClose();
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 z-[60] backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed inset-y-0 left-0 w-72 z-[70] shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col md:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ backgroundColor: 'var(--color-navbar)' }}
      >
        <div className="p-6 text-white flex justify-between items-center" style={{ backgroundColor: 'var(--color-primary)' }}>
          <div>
            <h2 className="font-bold text-xl font-serif">Menú</h2>
            <p className="text-xs opacity-80">Panel de Control</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full md:hidden bg-black/10 hover:bg-black/20"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-white/50 backdrop-blur-sm">
          
          {/* VISTAS DE COCINA Y ADMIN */}
          {(userRole === "admin" ||
            userRole === "kitchen" ||
            userRole === "cocina") && (
            <>
              <SidebarBtn
                active={activeView === "kitchen"}
                onClick={() => handleNav("kitchen")}
                icon={ChefHat}
                label="Cocina"
                desc="Ver producción del día"
                customStyle="bg-primary/10 border-l-4 border-primary"
                color="text-primary"
                bgColor="bg-white"
              />
              
              {/* DESPENSA - Toggle */}
              {modules.pantry !== false && (
                <SidebarBtn
                  active={activeView === "pantry"}
                  onClick={() => handleNav("pantry")}
                  icon={ShoppingBasket}
                  label="Insumos (Despensa)"
                  desc="Control de stock"
                  customStyle="bg-primary/10 border-l-4 border-primary"
                  color="text-primary"
                  bgColor="bg-white"
                />
              )}
              
              {/* FREEZER - Toggle */}
              {modules.freezer !== false && (
                <SidebarBtn
                  active={activeView === "freezer"}
                  onClick={() => handleNav("freezer")}
                  icon={Snowflake}
                  label="Freezer (Stock)"
                  desc="Lotes congelados"
                  customStyle="bg-primary/10 border-l-4 border-primary"
                  color="text-primary"
                  bgColor="bg-white"
                />
              )}
            </>
          )}

          {/* VISTAS EXCLUSIVAS DE ADMIN */}
          {userRole === "admin" && (
            <>
              <div className="border-t border-gray-100 my-2 pt-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 pl-2">
                  Gestión
                </p>
                <SidebarBtn
                  active={activeView === "orders"}
                  onClick={() => handleNav("orders")}
                  icon={ClipboardList}
                  label="Pedidos"
                  desc="Gestión de comandas"
                  customStyle="bg-primary/10 border-l-4 border-primary"
                  color="text-primary"
                  bgColor="bg-white"
                />
                
                {/* FINANZAS - Toggle */}
                {modules.finances !== false && (
                  <SidebarBtn
                    active={activeView === "stats"}
                    onClick={() => handleNav("stats")}
                    icon={BarChart3}
                    label="Finanzas"
                    desc="Caja y estadísticas"
                    customStyle="bg-primary/10 border-l-4 border-primary"
                    color="text-primary"
                    bgColor="bg-white"
                  />
                )}
                
                {/* CLIENTES - Toggle */}
                {modules.clients !== false && (
                  <SidebarBtn
                    active={activeView === "clients"}
                    onClick={() => handleNav("clients")}
                    icon={Users}
                    label="Clientes"
                    desc="Agenda y difusión"
                    customStyle="bg-primary/10 border-l-4 border-primary"
                    color="text-primary"
                    bgColor="bg-white"
                  />
                )}
                
                <SidebarBtn
                  active={activeView === "menu"}
                  onClick={() => handleNav("menu")}
                  icon={List}
                  label="Productos"
                  desc="Precios y recetas"
                  customStyle="bg-primary/10 border-l-4 border-primary"
                  color="text-primary"
                  bgColor="bg-white"
                />
                <SidebarBtn
                  active={activeView === "config"}
                  onClick={() => handleNav("config")}
                  icon={Settings}
                  label="Configuración"
                  desc="Ajustes del sistema"
                  customStyle="bg-primary/10 border-l-4 border-primary"
                  color="text-primary"
                  bgColor="bg-white"
                />
              </div>

              <div className="border-t border-gray-100 my-2 pt-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 pl-2">
                  Vista Cliente
                </p>
                <SidebarBtn
                  active={activeView === "public_store"}
                  onClick={() => handleNav("public_store")}
                  icon={Store}
                  label="Tienda Virtual"
                  desc="Realizar pedido manual"
                  customStyle="bg-green-50 border-l-4 border-green-500"
                  color="text-green-600"
                  bgColor="bg-white"
                />
                <SidebarBtn
                  active={activeView === "landing"}
                  onClick={() => handleNav("landing")}
                  icon={Globe}
                  label="Ver Landing Page"
                  desc="Portada del sitio web"
                  customStyle="bg-purple-50 border-l-4 border-purple-500"
                  color="text-purple-600"
                  bgColor="bg-white"
                />
              </div>
            </>
          )}

          {/* VISTA EXCLUSIVA DE CLIENTE */}
          {userRole === "cliente" && modules.courses !== false && ( 
            <SidebarBtn
              active={activeView === "my_courses"}
              onClick={() => handleNav("my_courses")}
              icon={BookOpen}
              label="Mis Cursos"
              desc="Acceso a tus clases"
              customStyle="bg-primary/10 border-l-4 border-primary"
              color="text-primary"
              bgColor="bg-white"
            />
          )}

          {/* BOTÓN INSTALAR APP */}
          {installPwa && (
            <div className="mb-4 bg-gray-50/50 p-2 rounded-xl border border-gray-100">
              <button
                onClick={installPwa}
                className="w-full text-white p-3 rounded-lg flex items-center gap-3 shadow-md transition-colors hover:brightness-90"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <Download size={20} />
                <div className="text-left">
                  <p className="font-bold text-sm">Instalar App</p>
                  <p className="text-[10px] opacity-90">Para acceso rápido</p>
                </div>
              </button>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 bg-white/80">
          <button
            onClick={onLogout}
            className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-red-50 text-red-600 font-bold transition-colors"
          >
            <LogOut className="w-5 h-5" /> Cerrar Sesión
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;